import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import toast, { Toaster } from 'react-hot-toast';
import { todayLocalStr } from '../utils/dateUtils';

export default function LoanForm() {
  var navigate = useNavigate();
  var _cust = useState([]), customers = _cust[0], setCustomers = _cust[1];
  var _accts = useState([]), accounts = _accts[0], setAccounts = _accts[1];
  var _cid = useState(''), customerId = _cid[0], setCustomerId = _cid[1];
  var _amt = useState(''), loanAmount = _amt[0], setLoanAmount = _amt[1];
  var _rate = useState('10'), interestRate = _rate[0], setInterestRate = _rate[1];
  var _freq = useState('monthly'), frequency = _freq[0], setFrequency = _freq[1];
  var _date = useState(todayLocalStr()), loanDate = _date[0], setLoanDate = _date[1];
  var _aid = useState(''), accountId = _aid[0], setAccountId = _aid[1];
  var _load = useState(false), loading = _load[0], setLoading = _load[1];
  var _deduct = useState(0), deductPending = _deduct[0], setDeductPending = _deduct[1];
  var _summary = useState(null), clientSummary = _summary[0], setClientSummary = _summary[1];
  // Customer type-ahead: fixes the request to search-as-you-type instead of
  // scrolling a long <select> of every customer.
  var _custSearch = useState(''), customerSearch = _custSearch[0], setCustomerSearch = _custSearch[1];
  var _showSuggestions = useState(false), showSuggestions = _showSuggestions[0], setShowSuggestions = _showSuggestions[1];

  useEffect(function() {
    api.get('/customers').then(function(r) { setCustomers(r.data); });
    api.get('/accounts').then(function(r) { setAccounts(r.data); });
  }, []);

  // Fetch client summary when customer selected
  function handleCustomerChange(cid) {
    setCustomerId(cid);
    setDeductPending(0);
    if (cid) {
      api.get('/customers/' + cid).then(function(r) {
        var c = r.data;
        var loans = c.loans || [];
        var totalGiven = loans.reduce(function(s, l) { return s + l.loan_amount; }, 0);
        var activeLoans = loans.filter(function(l) { return l.status === 'active'; });
        var totalActive = activeLoans.reduce(function(s, l) { return s + l.loan_amount; }, 0);
        var totalPending = 0; activeLoans.forEach(function(l) { totalPending += (parseFloat(l.total_outstanding) || 0); });
        setClientSummary({
          totalLoans: loans.length,
          totalGiven: totalGiven,
          activeLoans: activeLoans.length,
          totalActiveAmount: totalActive,
          totalPendingInterest: totalPending
        });
        setDeductPending(0);
      }).catch(function() {});
    } else {
      setClientSummary(null);
    }
  };

  var selectedCustomer = customers.find(function(c) { return c.id === parseInt(customerId); });

  function selectCustomer(c) {
    handleCustomerChange(String(c.id));
    setCustomerSearch(c.client_number + ' - ' + c.name + ' (' + c.phone1 + ')');
    setShowSuggestions(false);
  }

  function handleSearchChange(value) {
    setCustomerSearch(value);
    setShowSuggestions(true);
    // Typing again after a selection clears the actual selection until they
    // pick a match from the list, so the form can't silently submit against
    // stale customer data that no longer matches what's shown in the box.
    if (customerId) { setCustomerId(''); setClientSummary(null); }
  }

  var q = customerSearch.trim().toLowerCase();
  var filteredCustomers = q === '' ? customers.slice(0, 20) : customers.filter(function(c) {
    return (c.name && c.name.toLowerCase().indexOf(q) !== -1) ||
           (c.client_number && String(c.client_number).toLowerCase().indexOf(q) !== -1) ||
           (c.phone1 && c.phone1.indexOf(q) !== -1);
  }).slice(0, 20);

  var interestDeducted = (parseFloat(loanAmount) || 0) * ((parseFloat(interestRate) || 0) / 100);
  var amountToGive = (parseFloat(loanAmount) || 0) - interestDeducted - parseFloat(deductPending || 0);
  var selectedAccount = accounts.find(function(a) { return a.id === parseInt(accountId); });
  var fmt = function(n) { return '\u20B9' + (n || 0).toLocaleString('en-IN'); };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!customerId || !loanAmount || !accountId) { toast.error('Fill all required fields'); return; }
    if (selectedAccount && selectedAccount.balance < parseFloat(loanAmount)) { toast.error('Insufficient balance'); return; }
    setLoading(true);
    try {
      var res = await api.post('/loans', {
        customer_id: parseInt(customerId), loan_amount: parseFloat(loanAmount),
        interest_rate: parseFloat(interestRate), frequency: frequency, loan_date: loanDate, deduct_pending: deductPending,
        disbursed_from_account_id: parseInt(accountId)
      });
      
      // If deduct pending was selected, pay those pending entries
      if (parseFloat(deductPending) > 0 && clientSummary && clientSummary.totalPendingInterest > 0) {
        toast.success('Loan #' + res.data.id + ' created! Please manually collect pending interest of ' + fmt(deductPending) + ' from collections page.');
      } else {
        toast.success('Loan #' + res.data.id + ' created! Amount given: ' + fmt(amountToGive));
      }
      setTimeout(function() { navigate('/loans'); }, 2000);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return React.createElement('div', { style: { maxWidth: 650, margin: '0 auto' } },
    React.createElement(Toaster, { position: 'top-right' }),
    React.createElement('h2', { style: { fontSize: 40, fontWeight: 'bold', marginBottom: 24 } }, 'New Loan'),
    React.createElement('form', { onSubmit: handleSubmit, style: { background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' } },
      
      // Customer Selection
      React.createElement('div', { style: { marginBottom: 16, position: 'relative' } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Customer *'),
        React.createElement('input', {
          type: 'text',
          value: customerSearch,
          placeholder: 'Start typing a name, client #, or phone...',
          onChange: function(e) { handleSearchChange(e.target.value); },
          onFocus: function() { setShowSuggestions(true); },
          onBlur: function() { setTimeout(function() { setShowSuggestions(false); }, 150); },
          style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }
        }),
        // Hidden input carries the actual selected id so the browser's
        // native "required" validation still works against a real value.
        React.createElement('input', { type: 'hidden', value: customerId }),
        showSuggestions && React.createElement('div', {
          style: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'white', border: '1px solid #d1d5db', borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
        },
          filteredCustomers.length === 0
            ? React.createElement('div', { style: { padding: 12, color: '#6b7280', fontSize: 13 } }, 'No matching customers')
            : filteredCustomers.map(function(c) {
                return React.createElement('div', {
                  key: c.id,
                  onMouseDown: function(e) { e.preventDefault(); selectCustomer(c); },
                  style: { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 14 }
                },
                  React.createElement('div', { style: { fontWeight: 600 } }, c.name),
                  React.createElement('div', { style: { fontSize: 12, color: '#6b7280' } }, '#' + c.client_number + ' | ' + c.phone1)
                );
              })
        ),
        selectedCustomer && React.createElement('div', { style: { marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, background: '#f0f9ff', padding: 10, borderRadius: 8 } },
          selectedCustomer.photo_url
            ? React.createElement('img', { src: window.UPLOAD_BASE + selectedCustomer.photo_url, alt: '', style: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' } })
            : React.createElement('div', { style: { width: 80, height: 80, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 } }, '\u{1F464}'),
          React.createElement('div', null,
            React.createElement('div', { style: { fontWeight: 600 } }, selectedCustomer.name),
            React.createElement('div', { style: { fontSize: 13, color: '#6b7280' } }, '#' + selectedCustomer.client_number + ' | ' + (selectedCustomer.area || 'N/A'))
          )
        )
      ),

      // Client Summary
      clientSummary && React.createElement('div', { style: { marginBottom: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12 } },
        React.createElement('h4', { style: { fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#92400e' } }, 'Client Summary'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13 } },
          React.createElement('div', null, 'Total Loans: ', React.createElement('strong', null, clientSummary.totalLoans)),
          React.createElement('div', null, 'Total Given: ', React.createElement('strong', null, fmt(clientSummary.totalGiven))),
          React.createElement('div', null, 'Active Loans: ', React.createElement('strong', null, clientSummary.activeLoans)),
          React.createElement('div', null, 'Active Amount: ', React.createElement('strong', null, fmt(clientSummary.totalActiveAmount))),
          React.createElement('div', { style: { gridColumn: '1 / -1', color: '#dc2626' } }, 'Pending Interest: ', React.createElement('strong', null, fmt(clientSummary.totalPendingInterest)))
        ),
        clientSummary.totalPendingInterest > 0 && React.createElement('div', { style: { marginTop: 8 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: deductPending > 0, onChange: function(e) { setDeductPending(e.target.checked ? clientSummary.totalPendingInterest : 0); }, style: { width: 16, height: 16 } }),
            'Deduct pending interest from loan amount (max: ' + fmt(clientSummary.totalPendingInterest) + ')'
          ),
          deductPending > 0 && React.createElement('input', {
            type: 'number',
            value: deductPending,
            onChange: function(e) { var v = parseFloat(e.target.value) || 0; setDeductPending(Math.min(v, clientSummary.totalPendingInterest)); },
            max: clientSummary.totalPendingInterest,
            min: 0,
            style: { width: '100%', marginTop: 6, padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }
          })
        )
      ),

      // Loan Amount (no scroll)
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Loan Amount (Rs) *'),
        React.createElement('input', { type: 'number', value: loanAmount, onChange: function(e) { setLoanAmount(e.target.value); }, required: true, min: 1, style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', MozAppearance: 'textfield' }, onWheel: function(e) { e.target.blur(); } })
      ),
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Monthly Interest Rate (%) *'),
        React.createElement('input', { type: 'number', value: interestRate, onChange: function(e) { setInterestRate(e.target.value); }, required: true, step: 0.1, min: 0, style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', MozAppearance: 'textfield' }, onWheel: function(e) { e.target.blur(); } })
      ),
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Frequency'),
        React.createElement('select', { value: frequency, onChange: function(e) { setFrequency(e.target.value); }, style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' } },
          React.createElement('option', { value: 'monthly' }, 'Monthly'),
          React.createElement('option', { value: 'weekly' }, 'Weekly')
        )
      ),
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Loan Date'),
        React.createElement('input', { type: 'date', value: loanDate, onChange: function(e) { setLoanDate(e.target.value); }, style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' } })
      ),
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 4 } }, 'Disburse From *'),
        React.createElement('select', { value: accountId, onChange: function(e) { setAccountId(e.target.value); }, required: true, style: { width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' } },
          React.createElement('option', { value: '' }, 'Select Account'),
          accounts.map(function(a) { return React.createElement('option', { key: a.id, value: a.id }, a.name + ' (' + a.type + ') - ' + fmt(a.balance)); })
        )
      ),

      // Calculation
      parseFloat(loanAmount) > 0 && React.createElement('div', { style: { background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 } },
        React.createElement('h3', { style: { fontWeight: 600, marginBottom: 8 } }, 'Calculation'),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 4 } }, React.createElement('span', null, 'Loan Amount:'), React.createElement('span', { style: { fontWeight: 500 } }, fmt(parseFloat(loanAmount)))),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', color: '#dc2626', marginBottom: 4 } }, React.createElement('span', null, 'Interest Deducted (' + interestRate + '%):'), React.createElement('span', { style: { fontWeight: 500 } }, '-' + fmt(interestDeducted))),
        parseFloat(deductPending) > 0 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', color: '#dc2626', marginBottom: 4 } }, React.createElement('span', null, 'Pending Interest Deducted:'), React.createElement('span', { style: { fontWeight: 500 } }, '-' + fmt(parseFloat(deductPending)))),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: 8, color: '#16a34a', fontWeight: 'bold' } }, React.createElement('span', null, 'Amount to Give:'), React.createElement('span', null, fmt(amountToGive)))
      ),

      React.createElement('button', { type: 'submit', disabled: loading, style: { width: '100%', padding: 14, background: loading ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: loading ? 'default' : 'pointer' } },
        loading ? 'Processing...' : 'Confirm & Disburse Loan'
      )
    )
  );
}





